import {
  Clock,
  ShieldCheck,
  ShieldSlash,
  Globe,
  FileText,
  Calendar,
  CheckCircle,
  Prohibit,
  UserMinus,
  PauseCircle,
  XCircle,
  MinusCircle,
} from "@phosphor-icons/react";

/**
 * Therapist approval status (admin therapist list, therapist detail, therapist profile).
 */
export const THERAPIST_STATUS_CONFIG = {
  pending: {
    label: "Na čekanju",
    icon: Clock,
    color: "text-amber-600",
    bg: "bg-amber-50",
    dot: "bg-amber-500",
    ring: "ring-amber-500/20",
  },
  approved: {
    label: "Odobreni",
    icon: ShieldCheck,
    color: "text-emerald-700",
    bg: "bg-emerald-50",
    dot: "bg-emerald-500",
    ring: "ring-emerald-500/20",
  },
  rejected: {
    label: "Odbijeni",
    icon: ShieldSlash,
    color: "text-rose-600",
    bg: "bg-rose-50",
    dot: "bg-rose-500",
    ring: "ring-rose-500/20",
  },
} as const;

/**
 * Therapist status for singular context (detail page).
 * Same colors as THERAPIST_STATUS_CONFIG but singular labels.
 */
export const THERAPIST_STATUS_CONFIG_SINGULAR = {
  pending: {
    label: "Na čekanju",
    color: "text-amber-600",
    bg: "bg-amber-50",
    dot: "bg-amber-500",
    ring: "ring-amber-500/20",
  },
  approved: {
    label: "Odobren",
    color: "text-emerald-700",
    bg: "bg-emerald-50",
    dot: "bg-emerald-500",
    ring: "ring-emerald-500/20",
  },
  rejected: {
    label: "Odbijen",
    color: "text-rose-600",
    bg: "bg-rose-50",
    dot: "bg-rose-500",
    ring: "ring-rose-500/20",
  },
} as const;

/**
 * Therapist profile status (therapist's own profile page).
 * Slightly different ring values from the admin version.
 */
export const THERAPIST_PROFILE_STATUS_CONFIG = {
  pending: {
    label: "Na čekanju",
    bg: "bg-amber-50",
    color: "text-amber-700",
    ring: "ring-amber-200",
    dot: "bg-amber-500",
  },
  approved: {
    label: "Odobren",
    bg: "bg-emerald-50",
    color: "text-emerald-700",
    ring: "ring-emerald-200",
    dot: "bg-emerald-500",
  },
  rejected: {
    label: "Odbijen",
    bg: "bg-rose-50",
    color: "text-rose-700",
    ring: "ring-rose-200",
    dot: "bg-rose-500",
  },
};

/**
 * Blog post status (admin blog management).
 */
export const BLOG_STATUS_CONFIG = {
  pending: {
    label: "Na čekanju",
    dot: "bg-amber-500",
    color: "text-amber-600",
    bg: "bg-amber-50",
    ring: "ring-amber-500/20",
    icon: Clock,
  },
  published: {
    label: "Objavljeni",
    dot: "bg-emerald-500",
    color: "text-emerald-700",
    bg: "bg-emerald-50",
    ring: "ring-emerald-500/20",
    icon: Globe,
  },
  draft: {
    label: "Nacrti",
    dot: "bg-slate-400",
    color: "text-slate-600",
    bg: "bg-slate-50",
    ring: "ring-slate-400/20",
    icon: FileText,
  },
  rejected: {
    label: "Odbijeni",
    dot: "bg-rose-500",
    color: "text-rose-600",
    bg: "bg-rose-50",
    ring: "ring-rose-500/20",
    icon: ShieldSlash,
  },
} as const;

/**
 * Page status (admin pages management).
 */
export const PAGE_STATUS_CONFIG = {
  published: {
    label: "Objavljene",
    dot: "bg-emerald-500",
    color: "text-emerald-700",
    bg: "bg-emerald-50",
    ring: "ring-emerald-500/20",
    icon: Globe,
  },
  draft: {
    label: "Nacrti",
    dot: "bg-slate-400",
    color: "text-slate-600",
    bg: "bg-slate-50",
    ring: "ring-slate-400/20",
    icon: FileText,
  },
} as const;

/**
 * Therapy session status (admin therapies page).
 */
export const THERAPY_STATUS_CONFIG = {
  scheduled: {
    label: "Zakazana",
    color: "bg-blue-500/10 text-blue-600",
    dot: "bg-blue-500",
  },
  pending_outcome: {
    label: "Čeka evidenciju",
    color: "bg-amber-500/10 text-amber-700",
    dot: "bg-amber-500",
  },
  completed: {
    label: "Završena",
    color: "bg-emerald-500/10 text-emerald-600",
    dot: "bg-emerald-500",
  },
  cancelled: {
    label: "Otkazana",
    color: "bg-red-500/10 text-red-600",
    dot: "bg-red-500",
  },
  no_show: {
    label: "Nepojavljivanje",
    color: "bg-orange-500/10 text-orange-600",
    dot: "bg-orange-500",
  },
} as const;

/**
 * Therapy payment status (admin therapies page).
 */
export const THERAPY_PAYMENT_CONFIG = {
  pending: {
    label: "Na čekanju",
    color: "bg-amber-500/10 text-amber-600",
    dot: "bg-amber-500",
  },
  pending_transfer: {
    label: "Čeka uplatu",
    color: "bg-orange-500/10 text-orange-600",
    dot: "bg-orange-500",
  },
  paid: {
    label: "Plaćeno",
    color: "bg-emerald-500/10 text-emerald-600",
    dot: "bg-emerald-500",
  },
  failed: {
    label: "Neuspješno",
    color: "bg-red-500/10 text-red-600",
    dot: "bg-red-500",
  },
  refunded: {
    label: "Refundirano",
    color: "bg-blue-500/10 text-blue-600",
    dot: "bg-blue-500",
  },
} as const;

/**
 * Session status for therapist detail page (admin).
 */
export const SESSION_STATUS_CONFIG = {
  scheduled: { label: "Zakazano", color: "text-blue-700", bg: "bg-blue-50", icon: Calendar },
  pending_outcome: { label: "Čeka evidenciju", color: "text-amber-700", bg: "bg-amber-50", icon: Clock },
  completed: { label: "Završeno", color: "text-emerald-700", bg: "bg-emerald-50", icon: CheckCircle },
  cancelled: { label: "Otkazano", color: "text-rose-600", bg: "bg-rose-50", icon: Prohibit },
  no_show: { label: "Nije se pojavio/la", color: "text-slate-600", bg: "bg-slate-100", icon: UserMinus },
} as const;

/** Session status with singular i18n labels for per-session rows (admin dashboard, supervisor sessions list). */
export const getSessionStatusConfig = (
  t: (key: string) => string
): Record<string, { label: string; color: string; bg: string; icon: typeof Clock }> => ({
  scheduled: { label: t("status.scheduled"), color: "oklch(50% 0.15 250)", bg: "oklch(92% 0.04 250)", icon: Clock },
  completed: { label: t("status.completed"), color: "oklch(45% 0.15 155)", bg: "oklch(92% 0.05 155)", icon: CheckCircle },
  cancelled: { label: t("status.cancelled"), color: "oklch(55% 0.15 25)", bg: "oklch(93% 0.04 25)", icon: XCircle },
  no_show: { label: t("status.noShow"), color: "oklch(55% 0.05 84)", bg: "oklch(94% 0.01 84)", icon: XCircle },
});

/** Session status with plural i18n labels for aggregated breakdowns (supervisor dashboard status chart). */
export const getSessionStatusBreakdownConfig = (
  t: (key: string) => string
): Record<string, { label: string; color: string; bg: string; icon: typeof Clock }> => ({
  scheduled: { label: t("status.scheduledPlural"), color: "oklch(50% 0.15 250)", bg: "oklch(92% 0.04 250)", icon: Clock },
  completed: { label: t("status.completedPlural"), color: "oklch(45% 0.15 155)", bg: "oklch(92% 0.05 155)", icon: CheckCircle },
  cancelled: { label: t("status.cancelledPlural"), color: "oklch(55% 0.15 25)", bg: "oklch(93% 0.04 25)", icon: XCircle },
  no_show: { label: t("status.noShow"), color: "oklch(55% 0.05 84)", bg: "oklch(94% 0.01 84)", icon: XCircle },
});

/**
 * Session status for admin clients page.
 */
export const CLIENT_SESSION_STATUS_CONFIG = {
  scheduled: {
    label: "Zakazana",
    color: "text-blue-600",
    bg: "bg-blue-50",
    icon: Calendar,
  },
  pending_outcome: {
    label: "Čeka evidenciju",
    color: "text-amber-700",
    bg: "bg-amber-50",
    icon: Clock,
  },
  completed: {
    label: "Završena",
    color: "text-emerald-700",
    bg: "bg-emerald-50",
    icon: CheckCircle,
  },
  cancelled: {
    label: "Otkazana",
    color: "text-rose-600",
    bg: "bg-rose-50",
    icon: XCircle,
  },
  no_show: {
    label: "Nije došao",
    color: "text-amber-600",
    bg: "bg-amber-50",
    icon: MinusCircle,
  },
} as const;

/**
 * Company status (admin companies page).
 */
export const COMPANY_STATUS_CONFIG = {
  active: {
    label: "Aktivne",
    dot: "bg-emerald-500",
    color: "text-emerald-700",
    bg: "bg-emerald-50",
    ring: "ring-emerald-500/20",
    icon: CheckCircle,
  },
  suspended: {
    label: "Suspendirane",
    dot: "bg-rose-500",
    color: "text-rose-600",
    bg: "bg-rose-50",
    ring: "ring-rose-500/20",
    icon: PauseCircle,
  },
} as const;

/**
 * Therapist therapies status badge (therapist's own therapies page).
 */
export const THERAPIST_THERAPY_STATUS_BADGE: Record<
  string,
  { label: string; className: string }
> = {
  scheduled: {
    label: "Zakazana",
    className: "bg-blue-50 text-blue-700",
  },
  pending_outcome: {
    label: "Čeka evidenciju",
    className: "bg-amber-50 text-amber-700",
  },
  completed: {
    label: "Završena",
    className: "bg-emerald-50 text-emerald-700",
  },
  cancelled: {
    label: "Otkazana",
    className: "bg-rose-50 text-rose-700",
  },
};

/**
 * Client session status styles and labels (client sessions page).
 */
export const CLIENT_SESSION_STATUS_STYLES: Record<string, string> = {
  scheduled: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

export const CLIENT_SESSION_STATUS_LABELS: Record<string, string> = {
  scheduled: "Zakazana",
  completed: "Zavrsena",
  cancelled: "Otkazana",
};

export const CLIENT_PAYMENT_STATUS_STYLES: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  pending_transfer: "bg-orange-100 text-orange-800",
  paid: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  refunded: "bg-blue-100 text-blue-800",
};

export const CLIENT_PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: "Na čekanju",
  pending_transfer: "Čeka uplatu",
  pending_hold: "Čeka potvrdu i uplatu",
  paid: "Plaćeno",
  failed: "Neuspješno",
  refunded: "Refundirano",
};

/**
 * Client type filter config (admin clients page).
 */
export const CLIENT_TYPE_CONFIG = {
  all: { label: "Svi", dot: "bg-foreground/40" },
  company: { label: "Firmski", dot: "bg-blue-500" },
  independent: { label: "Samostalni", dot: "bg-emerald-500" },
} as const;

/**
 * Faktura firme (admin i supervizor). `overdue` nije status u bazi nego
 * izdata faktura kojoj je prošao rok plaćanja.
 */
export const COMPANY_INVOICE_STATUS_CONFIG = {
  draft: { label: "Nacrt", color: "text-slate-600", bg: "bg-slate-100", dot: "bg-slate-400" },
  issued: { label: "Izdata", color: "text-sky-700", bg: "bg-sky-50", dot: "bg-sky-500" },
  overdue: { label: "Dospjela", color: "text-rose-700", bg: "bg-rose-50", dot: "bg-rose-500" },
  paid: { label: "Plaćena", color: "text-emerald-700", bg: "bg-emerald-50", dot: "bg-emerald-500" },
  void: { label: "Stornirana", color: "text-muted-foreground", bg: "bg-muted", dot: "bg-muted-foreground/50" },
} as const;

/** Zahtjev supervizora za dokument za plaćanje. */
export const COMPANY_PAYMENT_REQUEST_STATUS_CONFIG = {
  pending: { label: "Na čekanju", color: "text-amber-700", bg: "bg-amber-50", dot: "bg-amber-500" },
  fulfilled: { label: "Obrađen", color: "text-emerald-700", bg: "bg-emerald-50", dot: "bg-emerald-500" },
  rejected: { label: "Odbijen", color: "text-rose-700", bg: "bg-rose-50", dot: "bg-rose-500" },
} as const;

export const COMPANY_PAYMENT_REQUEST_TYPE_LABEL = {
  credit_topup: "Predračun za dopunu kredita",
  interim_invoice: "Faktura za dosadašnji dio mjeseca",
} as const;

export const COMPANY_BILLING_MODEL_LABEL = {
  credit: "Unaprijed uplaćeni kredit",
  invoice: "Mjesečna faktura",
} as const;
